import type { ListWelloPolicyCodeAllDto } from '__generated__/api/unauth';
import { Button, Chip } from '@common/components';
import { CustomError, isEnumValue } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { uniq } from 'es-toolkit';
import { Fragment, useEffect, useId, useRef, useState } from 'react';
import { scroller } from 'react-scroll';

import {
  EXCEPTED_CODE,
  EXCEPTED_HIDDEN_CODE,
  UnauthQueryOptions,
  WELLO_POLICY_CODE_TYPE,
} from '@/api';
import { BottomNavPortal } from '@/app/_components/BottomNavPortal';
import { Image, LoadingPageTemplate } from '@/components/client';
import { SEARCH_PARAMS } from '@/constants';
import { useAuthStore } from '@/stores/AuthStore';

interface InterestPolicyFilterProps {
  onSubmit: (data: ListWelloPolicyCodeAllDto[]) => void;
  isSubmitting?: boolean;
  submitButtonLabel?: string;
  children?: React.ReactNode;
  initSelectedMetaData: ListWelloPolicyCodeAllDto[];
  allowOnlyC14Code?: boolean;
}

export const InterestPolicyFilter = ({
  onSubmit,
  isSubmitting,
  submitButtonLabel,
  children,
  initSelectedMetaData,
  allowOnlyC14Code,
}: InterestPolicyFilterProps) => {
  const id = useId();
  const isSensitiveInfo = useAuthStore(
    (state) => state.myInfo?.sensitiveInfoYN,
  );

  const [selectedMetaDataMap, setSelectedMetaDataMap] = useState(
    new Map(
      initSelectedMetaData.map((metaData) => {
        const { code } = metaData;

        if (!code) throw new Error('metaData.code가 없습니다.');

        return [code, metaData];
      }),
    ),
  );

  const selectedCodeList = [...selectedMetaDataMap.keys()];

  //! ⚠️ 상태 값으로 관리할 필요가 없는 값
  const subTitleCodeList: string[] = [];
  const prevSubTitleCodeList = useRef(subTitleCodeList);

  const [isClicked, setIsClicked] = useState(false);

  useEffect(() => {
    const newSubTitleCodeList = subTitleCodeList.filter(
      (code) => !prevSubTitleCodeList.current.includes(code),
    );

    const scrollTo = newSubTitleCodeList[newSubTitleCodeList.length - 1];

    //! 애니메이션 후 스크롤
    if (scrollTo) {
      const scrollTimmer = setTimeout(() => {
        if (!isClicked) return;

        scroller.scrollTo(scrollTo, {
          smooth: true,
          offset: -100,
        });
      }, 300);

      return () => {
        clearTimeout(scrollTimmer);
        prevSubTitleCodeList.current = subTitleCodeList;
      };
    }

    return () => {
      prevSubTitleCodeList.current = subTitleCodeList;
    };
  });

  const { data } = useSuspenseQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/wello-policy-code/all'](),
    staleTime: Infinity,
    gcTime: Infinity,
    select: ({ context }) => {
      const { contents } = context ?? {};

      if (!contents)
        throw new CustomError({ return_message: '정보가 없습니다.' });

      const boundedMetaData: ListWelloPolicyCodeAllDto[][] = [];

      const genderMetaData: ListWelloPolicyCodeAllDto[] = [];

      const regionMetaData: {
        mainRegion: ListWelloPolicyCodeAllDto[];
        subRegion: ListWelloPolicyCodeAllDto[];
      } = {
        mainRegion: [],
        subRegion: [],
      };

      contents.forEach((content) => {
        const { code } = content;
        let { level } = content;

        if (!code || !level) return;

        const isHidden =
          code.includes('99') && !isEnumValue(EXCEPTED_HIDDEN_CODE, code);

        if (isHidden) return;

        if (code?.includes(EXCEPTED_CODE.GENDER)) {
          if (level === 2) genderMetaData.push(content);

          return;
        }

        if (code.startsWith('C14')) level = level - 1;

        if (code?.includes(EXCEPTED_CODE.REGION)) {
          switch (level) {
            case 2:
              return regionMetaData.mainRegion.push(content);
            case 3:
              return regionMetaData.subRegion.push(content);
            default:
              return;
          }
        }

        if (level === 3 && code.startsWith(EXCEPTED_CODE.UNEMPLOYMENT)) return;

        if (level === 1) return boundedMetaData.push([content]);

        const rootMetaData = boundedMetaData.find(([rootMetaData]) => {
          const rootMetaDataCode = rootMetaData.code;

          return rootMetaDataCode ? code.includes(rootMetaDataCode) : false;
        });

        return rootMetaData?.push(content);
      });

      const boundedCustomPolicyMetaData: ListWelloPolicyCodeAllDto[][] = [];
      const boundedInterestMetaData = boundedMetaData.filter((metaDataList) => {
        const [rootMetaData] = metaDataList;

        if (
          rootMetaData.welloPolicyCodeTypeCd === WELLO_POLICY_CODE_TYPE.INTEREST
        ) {
          return true;
        } else {
          //! 관심 필터는 원래 없어서 여기서 예외처리
          if (!rootMetaData.welloPolicyCodeFilterCd) return false;

          boundedCustomPolicyMetaData.push(metaDataList);

          return false;
        }
      });

      return {
        customPolicyMetaDataList: boundedCustomPolicyMetaData.flat(),
        interestedCategoryMetaDataList: boundedInterestMetaData.flat(),
        genderMetaData,
        regionMetaData,
      };
    },
  });

  const interestedCategoryMetaDataList = allowOnlyC14Code
    ? data.interestedCategoryMetaDataList.filter(
        (metaData) => metaData.code && metaData.code.startsWith('C14'),
      )
    : data.interestedCategoryMetaDataList;

  const tempParentMetaDataMap = new Map<string, ListWelloPolicyCodeAllDto>();

  const applySelectedMap = () =>
    setSelectedMetaDataMap(
      (selectedMetaDataMap) => new Map(selectedMetaDataMap),
    );

  return !interestedCategoryMetaDataList ? (
    <LoadingPageTemplate />
  ) : (
    <form
      className="pb-96 mobile-tablet:pb-0"
      id={id}
      onSubmit={(e) => {
        e.preventDefault();

        onSubmit([...selectedMetaDataMap.values()]);
      }}
    >
      <dl className="m-0 flex flex-wrap items-start justify-start gap-6 px-20">
        {interestedCategoryMetaDataList?.map((metaData, index) => {
          const { code, value, parentCode: parent_code } = metaData;

          let { level } = metaData;

          if (!code || !level) return null;

          if (code.startsWith('C14')) level = level - 1;

          switch (level) {
            case 1: {
              tempParentMetaDataMap.set(code, metaData);

              return (
                <dt
                  className="mb-4 mt-26 flex w-full items-center gap-4 text-Gray-800 typo-body4"
                  id={code}
                >
                  {metaData.imageUrl ? (
                    <Image
                      alt=""
                      height={18}
                      src={metaData.imageUrl}
                      width={18}
                    />
                  ) : null}
                  {value}
                </dt>
              );
            }

            case 2: {
              if (!parent_code) return null;

              const rootMetaData = tempParentMetaDataMap.get(parent_code);

              if (!rootMetaData) return null;

              const isDuplicatable = rootMetaData.duplicateAvailYN;

              return (
                <dd key={index} className="m-0 w-fit">
                  <Chip
                    checked={selectedMetaDataMap.has(code)}
                    className="px-10 py-5 typo-body4"
                    onCheckedChange={(checked) => {
                      if (metaData.sensitiveInfoYN && !isSensitiveInfo) return;

                      //* 최신 선택된 코드 리스트를 가져오기 위한 함수
                      const getSelectedCodeList = () => [
                        ...selectedMetaDataMap.keys(),
                      ];

                      if (checked) {
                        if (isDuplicatable) {
                          selectedMetaDataMap.set(code, metaData);
                        } else {
                          const duplicatedCode = getSelectedCodeList().filter(
                            (code) => code.includes(parent_code),
                          );

                          duplicatedCode.forEach((code) => {
                            selectedMetaDataMap.delete(code);
                          });

                          selectedMetaDataMap.set(code, metaData);
                        }
                      } else {
                        getSelectedCodeList().forEach((selectedCode) => {
                          if (selectedCode.includes(code))
                            selectedMetaDataMap.delete(selectedCode);
                        });
                      }

                      //* 해당사항 없음에 대한 예외처리
                      if (code.includes(EXCEPTED_CODE.TARGET_CHARACTERISTICS)) {
                        const isTargetEmpty =
                          code ===
                          EXCEPTED_HIDDEN_CODE.TARGET_CHARACTERISTICS_EMPTY;

                        if (checked) {
                          //* 해당 사항 없음 선택시 다른 선택된 대상 특성 삭제
                          if (isTargetEmpty) {
                            getSelectedCodeList().forEach((code) => {
                              if (
                                code.includes(
                                  EXCEPTED_CODE.TARGET_CHARACTERISTICS,
                                ) &&
                                code !==
                                  EXCEPTED_HIDDEN_CODE.TARGET_CHARACTERISTICS_EMPTY
                              )
                                selectedMetaDataMap.delete(code);
                            });
                          } //* 다른 대상 특성 선택시 해당 사항 없음 삭제
                          else {
                            selectedMetaDataMap.delete(
                              EXCEPTED_HIDDEN_CODE.TARGET_CHARACTERISTICS_EMPTY,
                            );
                          }
                        } //* 대상 특성에 아무것도 선택하지 않았을 때 해당 사항 없음 추가
                        else {
                          const hasSelectedTargetCharacteristics =
                            getSelectedCodeList().some((code) =>
                              code.includes(
                                EXCEPTED_CODE.TARGET_CHARACTERISTICS,
                              ),
                            );

                          if (!hasSelectedTargetCharacteristics) {
                            const targetCharacteristicsEmptyMetaData =
                              interestedCategoryMetaDataList.find(
                                ({ code }) =>
                                  code ===
                                  EXCEPTED_HIDDEN_CODE.TARGET_CHARACTERISTICS_EMPTY,
                              );

                            if (!targetCharacteristicsEmptyMetaData)
                              throw new Error(
                                'meta data에 대상 특성 해당 없음이 없습니다.',
                              );

                            selectedMetaDataMap.set(
                              EXCEPTED_HIDDEN_CODE.TARGET_CHARACTERISTICS_EMPTY,
                              targetCharacteristicsEmptyMetaData,
                            );
                          }
                        }
                      }

                      applySelectedMap();
                    }}
                    onClick={() => {
                      if (metaData.sensitiveInfoYN) {
                        if (isSensitiveInfo) {
                          setIsClicked(true);
                        } else {
                          const url = new URL(window.location.href);

                          url.searchParams.set(
                            SEARCH_PARAMS.POLICY_FILTER,
                            'true',
                          );

                          history.replaceState(null, '', url.search);
                        }
                      }
                    }}
                  >
                    {value}
                  </Chip>
                </dd>
              );
            }

            default: {
              if (!parent_code) return null;

              const hasSelectedParentMetaData =
                selectedMetaDataMap.has(parent_code);

              if (!hasSelectedParentMetaData) return null;

              //! ⚠️ 서버에서 받아온 선택했던 값들은 메타 데이터 값의 정보들이 완벽하지 않음 (ex. duplicate_avail_yn가 모두 false로 나오는 등)
              const parentMetaData = selectedMetaDataMap.get(parent_code);

              if (!parentMetaData) return null;

              const isFirstChild = !subTitleCodeList.includes(parent_code);

              const isDuplicatable = parentMetaData.duplicateAvailYN;

              if (isFirstChild) {
                subTitleCodeList.push(parent_code);
              }

              return (
                <Fragment key={index}>
                  {isFirstChild ? (
                    <dt
                      className="mb-4 mt-26 w-full animate-fade-up text-Gray-800 duration-300 typo-body4"
                      id={parent_code}
                    >
                      {metaData.imageUrl ? (
                        <Image
                          height={18}
                          src={metaData.imageUrl}
                          width={18}
                          alt="필터 아이콘"
                          className="object-contain"
                        />
                      ) : null}
                      {parentMetaData.value}
                    </dt>
                  ) : null}
                  <dd className="m-0 w-fit animate-fade-up duration-300">
                    <Chip
                      checked={selectedMetaDataMap.has(code)}
                      className="px-10 py-5 typo-body4"
                      onChange={(e) => {
                        if (metaData.sensitiveInfoYN && !isSensitiveInfo)
                          return;

                        const { checked } = e.target;

                        if (checked) {
                          if (isDuplicatable) {
                            selectedMetaDataMap.set(code, metaData);
                          } else {
                            const duplicatedCode = selectedCodeList.filter(
                              (code) =>
                                code !== parent_code &&
                                code.includes(parent_code),
                            );

                            duplicatedCode.forEach((code) => {
                              selectedMetaDataMap.delete(code);
                            });

                            selectedMetaDataMap.set(code, metaData);
                          }
                        } else {
                          selectedCodeList.forEach((selectedCode) => {
                            if (selectedCode.includes(code))
                              selectedMetaDataMap.delete(selectedCode);
                          });
                        }

                        applySelectedMap();
                      }}
                      onClick={() => {
                        if (metaData.sensitiveInfoYN) {
                          if (isSensitiveInfo) {
                            setIsClicked(true);
                          } else {
                            const url = new URL(window.location.href);

                            url.searchParams.set(
                              SEARCH_PARAMS.POLICY_FILTER,
                              'true',
                            );

                            history.replaceState(null, '', url.search);
                          }
                        }
                      }}
                    >
                      {value}
                    </Chip>
                  </dd>
                </Fragment>
              );
            }
          }
        })}
      </dl>
      <BottomNavPortal hasShadow={false}>
        <footer className="flex w-full gap-8 px-20 py-16">
          {children}
          <Button
            className="flex-1"
            disabled={(() => {
              if (isSubmitting) return true;

              const selectedParentCodeOfCodeList: string[] = [];

              const hasInterestCode = selectedCodeList.some((code) =>
                code.startsWith('C14'),
              );

              if (!hasInterestCode) return true;

              selectedMetaDataMap.forEach((metaData) => {
                const { parentCode: parent_code } = metaData;
                if (parent_code) selectedParentCodeOfCodeList.push(parent_code);
              });

              //* 선택안한 3차 이상 메타데이터 확인
              const tempHasChildrenSubMetaCodeList: string[] = [];

              interestedCategoryMetaDataList.forEach(
                ({ level, parentCode: parent_code }) => {
                  if (2 < (level ?? 0) && parent_code)
                    tempHasChildrenSubMetaCodeList.push(parent_code);
                },
              );

              const hasChildrenSubMetaCodeList = uniq(
                tempHasChildrenSubMetaCodeList,
              );

              for (const [code, metaCode] of selectedMetaDataMap.entries()) {
                if ((metaCode.level ?? 0) < 2) continue;

                const hasChildren = hasChildrenSubMetaCodeList.includes(code);

                if (!hasChildren) continue;

                const isChildrenSelected =
                  selectedParentCodeOfCodeList.includes(code);

                if (!isChildrenSelected) return true;
              }

              return false;
            })()}
            form={id}
            loading={isSubmitting}
            type="submit"
          >
            {submitButtonLabel}
          </Button>
        </footer>
      </BottomNavPortal>
    </form>
  );
};
